/**
 * An address box that completes itself from Google Places.
 *
 * A person types "5940 Singleton" and picks the suggestion; the component
 * hands back the whole address broken into its parts — street, city, state,
 * postal code, country — plus the coordinates Google geocoded it to. That
 * matters more than the convenience: a postal code typed by hand is the field
 * most often left blank or wrong, and coordinates read off the picked place
 * are exact, where geocoding the same text later is a guess.
 *
 * It degrades rather than breaking. With no Maps key, or when the Places
 * library will not load, it is a plain text input that still reports what was
 * typed — an address box that stops accepting typing because a third party is
 * unavailable would be worse than one that never suggested anything.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import { loadMapsScript, mapsLibraries } from '@/services/googleMapsLoader'
import { readAddressComponents, type ResolvedAddress } from './address'

export type { ResolvedAddress }


type Props = {
  value: string
  onChange: (text: string) => void
  /** Fired only when a suggestion is picked and its parts have been read. */
  onResolved?: (address: ResolvedAddress) => void
  apiKey?: string | null
  placeholder?: string
  className?: string
  id?: string
  disabled?: boolean
  /** Bias suggestions to a country, e.g. 'us'. */
  country?: string
}

type Suggestion = { id: string; label: string; placeId: string }

export function AddressAutocomplete({
  value,
  onChange,
  onResolved,
  apiKey,
  placeholder = 'Start typing an address…',
  className,
  id,
  disabled,
  country = 'us',
}: Props) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const [ready, setReady] = useState(false)
  const [open, setOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [active, setActive] = useState(-1)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const placesRef = useRef<google.maps.places.PlacesService | null>(null)
  const sessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  // Suggestions arrive out of order; only the newest query may render.
  const queryIdRef = useRef(0)

  useEffect(() => {
    if (!apiKey) return
    let cancelled = false
    ;(async () => {
      try {
        await loadMapsScript(apiKey)
        const libs = await mapsLibraries()
        if (cancelled || !libs.places) return
        serviceRef.current = new libs.places.AutocompleteService()
        // PlacesService needs a node to attach to; it never renders into it.
        placesRef.current = new libs.places.PlacesService(document.createElement('div'))
        sessionRef.current = new libs.places.AutocompleteSessionToken()
        setReady(true)
      } catch {
        // A plain text box is the fallback, and it is already rendered.
        setReady(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiKey])

  // Close on a click anywhere else.
  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const options = useMemo(
    () => ({
      componentRestrictions: country ? { country } : undefined,
      // Street addresses and named places both: a property is often searched
      // for by its name ("The Chelsea Norcross") before anyone has the number.
      types: ['geocode', 'establishment'],
    }),
    [country],
  )

  function query(text: string) {
    const service = serviceRef.current
    if (!service || text.trim().length < 3) {
      setSuggestions([])
      setOpen(false)
      return
    }
    const queryId = ++queryIdRef.current
    service.getPlacePredictions(
      { input: text, sessionToken: sessionRef.current ?? undefined, ...options },
      (predictions, status) => {
        if (queryId !== queryIdRef.current) return
        const ok = status === google.maps.places.PlacesServiceStatus.OK
        const rows = ok && predictions ? predictions : []
        setSuggestions(
          rows.slice(0, 6).map((p) => ({ id: p.place_id, label: p.description, placeId: p.place_id })),
        )
        setActive(-1)
        setOpen(rows.length > 0)
      },
    )
  }

  function choose(suggestion: Suggestion) {
    setOpen(false)
    setSuggestions([])
    const places = placesRef.current
    if (!places) {
      onChange(suggestion.label)
      return
    }
    places.getDetails(
      {
        placeId: suggestion.placeId,
        fields: ['address_components', 'formatted_address', 'geometry'],
        sessionToken: sessionRef.current ?? undefined,
      },
      (place, status) => {
        // A new session token per completed lookup: Google bills a session as
        // one unit from first keystroke to details call, and reusing a spent
        // token bills every later keystroke separately.
        sessionRef.current = new google.maps.places.AutocompleteSessionToken()
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          onChange(suggestion.label)
          return
        }
        const resolved = readAddressComponents(place)
        onChange(resolved.formatted || suggestion.label)
        onResolved?.(resolved)
      },
    )
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((i) => (i + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault()
      choose(suggestions[active])
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <input
        id={inputId}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={`${inputId}-suggestions`}
        onChange={(e) => {
          onChange(e.target.value)
          if (ready) query(e.target.value)
        }}
        onKeyDown={onKeyDown}
        className={clsx(
          'w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40',
          className,
        )}
      />
      {open && suggestions.length > 0 ? (
        <ul
          id={`${inputId}-suggestions`}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded border border-amber-300/20 bg-[#111110] py-1 shadow-lg"
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.id} role="option" aria-selected={index === active}>
              <button
                type="button"
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(suggestion)}
                className={clsx(
                  'block w-full truncate px-2.5 py-1.5 text-left text-[11px]',
                  index === active ? 'bg-amber-300/15 text-amber-100' : 'text-stone-300 hover:bg-white/[0.04]',
                )}
              >
                {suggestion.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
